import React, { useMemo, useState } from "react";
import {
  Badge,
  Box,
  Collapse,
  IconButton,
  InputAdornment,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AddCommentOutlined,
  ExpandLess,
  ExpandMore,
  AddBoxOutlined,
  ExploreOutlined,
  Search,
  Tag,
} from "@mui/icons-material";
import { ChatConversation } from "../../models/Chat";
import User from "../../models/User";
import { ConversationAvatar } from "./ChatAvatar";
import { conversationTitle, formatListTime, userDisplayName } from "./chatUtils";
import { TeamsConnectionPanel } from "./TeamsConnection";

interface ConversationListProps {
  conversations: ChatConversation[];
  activeId: number | null;
  meId: number;
  usersById: Map<number, User>;
  onSelect: (conversation: ChatConversation) => void;
  onNewChat: () => void;
  onNewChannel: () => void;
  onBrowseChannels: () => void;
  /** Extra header buttons, e.g. expand/close when shown in the sidebar. */
  extraActions?: React.ReactNode;
}

function previewText(conversation: ChatConversation, meId: number, usersById: Map<number, User>) {
  const last = conversation.lastMessage;
  if (!last) {
    return conversation.type === "channel" ? conversation.description || "No messages yet" : "No messages yet";
  }
  const author =
    last.authorId === meId
      ? "You"
      : conversation.type === "direct"
        ? ""
        : userDisplayName(usersById.get(last.authorId ?? 0)).split(" ")[0];
  const body = last.body.trim()
    ? last.body.replace(/\s+/g, " ")
    : last.attachmentCount > 0
      ? `Sent ${last.attachmentCount === 1 ? "a file" : `${last.attachmentCount} files`}`
      : "";
  return author ? `${author}: ${body}` : body;
}

const ConversationRow: React.FC<{
  conversation: ChatConversation;
  active: boolean;
  meId: number;
  usersById: Map<number, User>;
  onSelect: () => void;
}> = ({ conversation, active, meId, usersById, onSelect }) => {
  const unread = conversation.unreadCount > 0;
  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1.25}
      onClick={onSelect}
      sx={{
        px: 1.5,
        py: 0.9,
        mx: 0.75,
        borderRadius: 1,
        cursor: "pointer",
        bgcolor: active ? "background.paper" : undefined,
        boxShadow: active ? "0 1px 2px rgba(0,0,0,0.12)" : undefined,
        "&:hover": { bgcolor: active ? "background.paper" : "rgba(0,0,0,0.04)" },
      }}
    >
      <ConversationAvatar conversation={conversation} meId={meId} usersById={usersById} size={32} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="baseline" gap={1}>
          <Typography
            variant="body2"
            noWrap
            sx={{ flex: 1, minWidth: 0, fontWeight: unread ? 700 : 500 }}
          >
            {conversationTitle(conversation, meId, usersById)}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
            {formatListTime(conversation.lastMessage?.createdAt ?? null)}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography
            variant="caption"
            noWrap
            component="div"
            sx={{
              flex: 1,
              minWidth: 0,
              color: unread ? "text.primary" : "text.secondary",
              fontWeight: unread ? 600 : 400,
            }}
          >
            {previewText(conversation, meId, usersById)}
          </Typography>
          {conversation.mentionCount > 0 ? (
            <Badge badgeContent="@" color="error" sx={{ mr: 1 }} />
          ) : unread ? (
            <Badge badgeContent={conversation.unreadCount} color="primary" max={99} sx={{ mr: 1.25 }} />
          ) : null}
        </Stack>
      </Box>
    </Stack>
  );
};

const Section: React.FC<{
  title: string;
  open: boolean;
  onToggle: () => void;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, open, onToggle, action, children }) => (
  <Box sx={{ mb: 1 }}>
    <Stack
      direction="row"
      alignItems="center"
      sx={{ px: 1.5, py: 0.25, cursor: "pointer", userSelect: "none" }}
      onClick={onToggle}
    >
      {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
      <Typography variant="caption" fontWeight={700} sx={{ ml: 0.5, flex: 1, letterSpacing: 0.3 }}>
        {title}
      </Typography>
      {action}
    </Stack>
    <Collapse in={open}>{children}</Collapse>
  </Box>
);

const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  activeId,
  meId,
  usersById,
  onSelect,
  onNewChat,
  onNewChannel,
  onBrowseChannels,
  extraActions,
}) => {
  const [filter, setFilter] = useState("");
  const [chatsOpen, setChatsOpen] = useState(true);
  const [channelsOpen, setChannelsOpen] = useState(true);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const { chats, channels } = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const matches = (c: ChatConversation) =>
      !q || conversationTitle(c, meId, usersById).toLowerCase().includes(q);
    const visible = conversations.filter(
      (c) => (c.joined || c.id === activeId) && !c.archivedAt && matches(c)
    );
    return {
      chats: visible.filter((c) => c.type !== "channel"),
      channels: visible
        .filter((c) => c.type === "channel")
        .sort((a, b) => a.name.localeCompare(b.name)),
    };
  }, [conversations, filter, meId, usersById, activeId]);

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", bgcolor: "#f0f0f0" }}>
      <Stack direction="row" alignItems="center" sx={{ px: 2, pt: 1.5, pb: 1 }}>
        <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>
          Chat
        </Typography>
        <Tooltip title="New chat">
          <IconButton size="small" onClick={onNewChat}>
            <AddCommentOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Channels">
          <IconButton size="small" onClick={(event) => setMenuAnchor(event.currentTarget)}>
            <Tag fontSize="small" />
          </IconButton>
        </Tooltip>
        <Menu open={Boolean(menuAnchor)} anchorEl={menuAnchor} onClose={() => setMenuAnchor(null)}>
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onNewChannel();
            }}
          >
            <ListItemIcon>
              <AddBoxOutlined fontSize="small" />
            </ListItemIcon>
            Create channel
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onBrowseChannels();
            }}
          >
            <ListItemIcon>
              <ExploreOutlined fontSize="small" />
            </ListItemIcon>
            Browse channels
          </MenuItem>
        </Menu>
        {extraActions}
      </Stack>
      <Box sx={{ px: 1.5, pb: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search chats and channels"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
              sx: { bgcolor: "background.paper" },
            },
          }}
        />
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pb: 2 }}>
        <Section title="Chats" open={chatsOpen} onToggle={() => setChatsOpen((v) => !v)}>
          {chats.length === 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ px: 3, display: "block", py: 0.5 }}>
              {filter ? "No matching chats" : "Start a chat with the button above."}
            </Typography>
          ) : (
            chats.map((conversation) => (
              <ConversationRow
                key={conversation.id}
                conversation={conversation}
                active={conversation.id === activeId}
                meId={meId}
                usersById={usersById}
                onSelect={() => onSelect(conversation)}
              />
            ))
          )}
        </Section>
        <Section
          title="Channels"
          open={channelsOpen}
          onToggle={() => setChannelsOpen((v) => !v)}
          action={
            <Tooltip title="Browse channels">
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  onBrowseChannels();
                }}
              >
                <ExploreOutlined sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          }
        >
          {channels.length === 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ px: 3, display: "block", py: 0.5 }}>
              {filter ? "No matching channels" : "Join or create a channel."}
            </Typography>
          ) : (
            channels.map((conversation) => (
              <ConversationRow
                key={conversation.id}
                conversation={conversation}
                active={conversation.id === activeId}
                meId={meId}
                usersById={usersById}
                onSelect={() => onSelect(conversation)}
              />
            ))
          )}
        </Section>
      </Box>
      <TeamsConnectionPanel />
    </Box>
  );
};

export default ConversationList;
